import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { blogAPI, menuAPI, settingsAPI } from '../utils/api';
import { setSEO } from '../utils/seo';
import AdUnit from '../components/AdUnit';
import BannerSlider from '../components/BannerSlider';
import LocationPopup from '../components/LocationPopup';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TAG_COLORS = {
  'phu quoc': { bg: '#f5e2e1', color: '#922018' },
  'food': { bg: '#f6ecd0', color: '#6e5200' },
  'hoi an': { bg: '#dff0e6', color: '#186240' },
  'sapa': { bg: '#dff0e6', color: '#186240' },
  'north vietnam': { bg: '#dde9f6', color: '#104380' },
  'central vietnam': { bg: '#dff0e6', color: '#186240' },
  'itineraries': { bg: '#dde9f6', color: '#104380' },
  'dining': { bg: '#f6ecd0', color: '#6e5200' },
  'travel tips': { bg: '#efebe2', color: '#5c5650' },
};

const getTagStyle = (tag) => {
  const key = (tag || '').toLowerCase();
  return TAG_COLORS[key] || { bg: '#efebe2', color: '#5c5650' };
};

const getInitials = (name) => {
  if (!name) return 'WV';
  return name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();
};

const AVATAR_GRADIENTS = [
  'linear-gradient(135deg, #e8a824, #c4291c)',
  'linear-gradient(135deg, #6d65c9, #453a9e)',
  'linear-gradient(135deg, #82b048, #2f5a0e)',
  'linear-gradient(135deg, #72a6d9, #0a3a6b)',
  'linear-gradient(135deg, #4db890, #064538)',
  'linear-gradient(135deg, #e5b05e, #9e6210)',
];

const LandingPage = () => {
  const [posts, setPosts] = useState([]);
  const [topDishes, setTopDishes] = useState([]);
  const [activeFilter, setActiveFilter] = useState('All');
  const [siteSettings, setSiteSettings] = useState(null);
  const [nlEmail, setNlEmail] = useState('');
  const [nlStatus, setNlStatus] = useState(''); // '' | 'sending' | 'success' | 'existing' | 'error'

  useEffect(() => {
    setSEO({
      title: 'Benz Eatery | Best Restaurant in Phu Quoc | Vietnamese Cuisine & Seafood',
      description: 'Benz Eatery - #1 restaurant in Phu Quoc, Vietnam. Must-try authentic Vietnamese cuisine, fresh seafood, pho, banh mi & international dishes. Order online or visit us on Phu Quoc Island.',
      ogType: 'restaurant',
      canonical: 'https://benzeatery.com/',
    });
    blogAPI.getAll(true).then(res => setPosts(res.data)).catch(() => {});
    settingsAPI.get().then(res => setSiteSettings(res.data)).catch(() => {});
    // Load promoted / top dishes for the restaurant showcase section.
    menuAPI.getPromoted()
      .then(res => setTopDishes((res.data || []).slice(0, 8)))
      .catch(() => {
        // Fallback: if no promoted items, grab the first 8 available items
        menuAPI.getAll()
          .then(res2 => {
            const available = (res2.data || []).filter(i => i.available !== false);
            setTopDishes(available.slice(0, 8));
          })
          .catch(() => {});
      });
  }, []);

  // Admin-controlled landing page section visibility (stored in settings.landing_sections).
  // Defaults: everything visible. Admin can hide any section.
  const sections = useMemo(() => ({
    top_dishes: siteSettings?.landing_sections?.top_dishes !== false,
    blog: siteSettings?.landing_sections?.blog !== false,
    spotlight: siteSettings?.landing_sections?.spotlight !== false,
    travel_tips: siteSettings?.landing_sections?.travel_tips !== false,
    popular: siteSettings?.landing_sections?.popular !== false,
    newsletter: siteSettings?.landing_sections?.newsletter !== false,
  }), [siteSettings]);

  const allTags = useMemo(() => {
    const tags = new Set();
    posts.forEach(p => (p.tags || []).forEach(t => tags.add(t)));
    return ['All', ...Array.from(tags)];
  }, [posts]);

  const filteredPosts = useMemo(() => {
    if (activeFilter === 'All') return posts;
    return posts.filter(p => (p.tags || []).some(t => t.toLowerCase() === activeFilter.toLowerCase()));
  }, [posts, activeFilter]);

  const featuredPosts = useMemo(() => {
    const featured = posts.filter(p => p.featured);
    // If no posts are explicitly featured, use the latest 5 with covers
    if (featured.length === 0) return posts.filter(p => p.cover_image).slice(0, 5);
    return featured;
  }, [posts]);

  const featuredPost = featuredPosts[0] || posts[0];

  const gridPosts = filteredPosts.filter(p => {
    if (filteredPosts.length <= 3) return true;
    return !featuredPost || p.id !== featuredPost.id;
  });

  // Hero slider
  const [heroIndex, setHeroIndex] = useState(0);
  const heroCount = featuredPosts.length;

  const nextSlide = useCallback(() => {
    setHeroIndex(prev => (prev + 1) % Math.max(heroCount, 1));
  }, [heroCount]);

  useEffect(() => {
    if (heroCount <= 1) return;
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [heroCount, nextSlide]);

  const getCover = (post) => {
    if (!post?.cover_image) return null;
    return post.cover_image.startsWith('/') ? `${API_URL}${post.cover_image}` : post.cover_image;
  };

  const fmtDate = (d) => {
    if (!d) return '';
    const date = new Date(d);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const fmtDateLong = (d) => {
    if (!d) return '';
    const date = new Date(d);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="wv-root" data-testid="landing-page">
      {/* Announcement */}
      <div className="wv-announce" data-testid="announcement-bar">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{display:'inline',verticalAlign:'middle',marginRight:6}}><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
        New guide out now — <span className="wv-announce-hl">7 days in Phu Quoc on any budget →</span>
      </div>

      {/* Nav */}
      <nav className="wv-nav" data-testid="landing-nav">
        <div className="wv-nav-inner">
          <Link to="/" className="wv-logo" data-testid="logo-link">
            <div className="wv-logo-mark">
              {siteSettings?.restaurant_logo ? (
                <img src={siteSettings.restaurant_logo.startsWith('/') ? `${API_URL}${siteSettings.restaurant_logo}` : siteSettings.restaurant_logo} alt="Logo" className="wv-logo-img" />
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
              )}
            </div>
            <div>
              <span>Wander Vietnam</span>
              <span className="wv-powered-by">Powered by Benz Eatery</span>
            </div>
          </Link>
          <div className="wv-nav-links">
            <Link to="/" className="active">Home</Link>
            <a href="#destinations">Destinations</a>
            <a href="#spotlight">Phu Quoc</a>
            <a href="#articles">Food</a>
            <a href="#tips">Travel tips</a>
          </div>
          <div className="wv-nav-actions">
            <div className="wv-search">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
              Search trips...
            </div>
            <Link to="/menu" className="wv-plan-btn" data-testid="nav-menu-link">Our Restaurant</Link>
          </div>
        </div>
      </nav>

      {/* Hero Slider */}
      {featuredPosts.length > 0 && (
        <section className="wv-hero" data-testid="hero-section">
          <div className="wv-hero-slider">
            {featuredPosts.map((post, i) => (
              <Link
                to={`/blog/${post.id}`}
                className={`wv-hero-card ${i === heroIndex ? 'active' : ''}`}
                key={post.id}
                data-testid={`hero-slide-${i}`}
              >
                {getCover(post) ? (
                  <img src={getCover(post)} alt={post.title} />
                ) : (
                  <img src="https://images.unsplash.com/photo-1528127269322-539801943592?w=1600&auto=format&fit=crop&q=80" alt="Vietnam" />
                )}
                <div className="wv-hero-overlay" />
                <div className="wv-hero-content">
                  <div className="wv-hero-kicker">
                    <span className="wv-hero-dot" />
                    Featured guide{post.tags?.[0] ? ` · ${post.tags[0]}` : ''}
                  </div>
                  <h1>{post.title}</h1>
                  <p>{post.excerpt}</p>
                  <div className="wv-hero-meta">
                    <div className="wv-avatar">{getInitials(post.author)}</div>
                    <span style={{ fontWeight: 500 }}>{post.author || 'Wander Vietnam'}</span>
                    <span>·</span>
                    <span>{fmtDateLong(post.created_at)}</span>
                    {post.read_time && <><span>·</span><span>{post.read_time}</span></>}
                  </div>
                </div>
              </Link>
            ))}
            {heroCount > 1 && (
              <div className="wv-hero-nav" data-testid="hero-nav">
                <button className="wv-hero-arrow" onClick={(e) => { e.preventDefault(); setHeroIndex((heroIndex - 1 + heroCount) % heroCount); }} data-testid="hero-prev">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6"/></svg>
                </button>
                <div className="wv-hero-dots">
                  {featuredPosts.map((_, i) => (
                    <button
                      key={i}
                      className={`wv-hero-dot-btn ${i === heroIndex ? 'active' : ''}`}
                      onClick={(e) => { e.preventDefault(); setHeroIndex(i); }}
                      data-testid={`hero-dot-${i}`}
                    />
                  ))}
                </div>
                <button className="wv-hero-arrow" onClick={(e) => { e.preventDefault(); setHeroIndex((heroIndex + 1) % heroCount); }} data-testid="hero-next">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Top Dishes — restaurant-focused showcase (replaces old travel destinations) */}
      {sections.top_dishes && topDishes.length > 0 && (
        <section className="wv-explore" id="destinations" data-testid="top-dishes-section">
          <div className="wv-section-head">
            <h2 className="wv-section-title">Our Top Dishes</h2>
            <Link to="/menu" className="wv-section-link" data-testid="top-dishes-view-all">View full menu →</Link>
          </div>
          <div className="wv-destinations">
            {topDishes.map(dish => {
              const dishName = typeof dish.name === 'string'
                ? dish.name
                : (dish.name?.en || dish.name?.vi || Object.values(dish.name || {})[0] || 'Dish');
              const img = dish.image_url || 'https://images.unsplash.com/photo-1540189549336-e6e99c3679fe?w=600&auto=format&fit=crop&q=80';
              return (
                <Link
                  to="/menu"
                  className="wv-dest"
                  key={dish.id}
                  data-testid={`top-dish-${dish.id}`}
                >
                  <img src={img} alt={dishName} />
                  <div className="wv-dest-label">
                    <strong>{dishName}</strong>
                    <span>{dish.category || 'Chef\'s pick'}</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* In-house Banner: Between destinations and articles */}
      <div className="wv-ad-container" style={{ maxWidth: 1240, margin: '0 auto', padding: '8px 32px' }}>
        <BannerSlider position="landing" />
        <AdUnit variant="banner" />
      </div>

      {/* Filter bar + Articles — admin-togglable Blog section */}
      {sections.blog && (
      <>
      <div className="wv-filter-section" id="articles" data-testid="filter-section">
        <h2 className="wv-section-title">Latest stories</h2>
        <div className="wv-filters">
          {allTags.map(tag => (
            <button
              key={tag}
              className={`wv-filter-pill ${activeFilter === tag ? 'active' : ''}`}
              onClick={() => setActiveFilter(tag)}
              data-testid={`filter-${tag.toLowerCase().replace(/\s/g, '-')}`}
            >
              {tag}
            </button>
          ))}
        </div>
      </div>

      {/* Articles grid */}
      <section className="wv-articles" data-testid="articles-section">
        {gridPosts.map((post, i) => (
          <Link to={`/blog/${post.id}`} className="wv-article" key={post.id} data-testid={`article-${post.id}`}>
            <div className="wv-article-img">
              {getCover(post) ? (
                <img src={getCover(post)} alt={post.title} />
              ) : (
                <img src="https://images.unsplash.com/photo-1528127269322-539801943592?w=800&auto=format&fit=crop&q=80" alt={post.title} />
              )}
            </div>
            {post.tags?.[0] && (
              <span className="wv-tag" style={{ background: getTagStyle(post.tags[0]).bg, color: getTagStyle(post.tags[0]).color }}>
                {post.tags[0]}
              </span>
            )}
            <h2>{post.title}</h2>
            <p>{post.excerpt}</p>
            <div className="wv-meta">
              <div className="wv-avatar wv-avatar-sm" style={{ background: AVATAR_GRADIENTS[i % AVATAR_GRADIENTS.length] }}>
                {getInitials(post.author)}
              </div>
              <span className="wv-meta-author">{post.author || 'Wander Vietnam'}</span>
              <span className="wv-meta-dot">·</span>
              <span>{fmtDate(post.created_at)}</span>
              {post.read_time && <><span className="wv-meta-dot">·</span><span>{post.read_time}</span></>}
            </div>
          </Link>
        ))}
        {gridPosts.length === 0 && posts.length > 0 && (
          <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '60px 0', color: '#6b6660' }}>
            No stories match this filter.
          </div>
        )}
        {posts.length === 0 && (
          <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '60px 0', color: '#6b6660' }}>
            No stories published yet. Check back soon!
          </div>
        )}
      </section>
      </>
      )}

      {/* Phu Quoc Spotlight */}
      {sections.spotlight && (
      <section className="wv-spotlight" id="spotlight" data-testid="spotlight-section">
        <div className="wv-spotlight-inner">
          <div className="wv-spotlight-img">
            <img src="https://images.unsplash.com/photo-1540541338287-41700207dee6?w=1200&auto=format&fit=crop&q=80" alt="Phu Quoc" />
          </div>
          <div className="wv-spotlight-body">
            <div className="wv-spotlight-kicker"><svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" style={{marginRight:6,verticalAlign:'middle'}}><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>Island spotlight</div>
            <h2>Phu Quoc, Vietnam's pearl island</h2>
            <p>White-sand beaches, a jungle national park, the world's longest over-sea cable car, and some of the best seafood in Southeast Asia — all on one island you can circle in a day.</p>
            <div className="wv-spotlight-stats">
              <div><div className="wv-stat-num">574 km²</div><div className="wv-stat-label">Island area</div></div>
              <div><div className="wv-stat-num">150+</div><div className="wv-stat-label">Beaches & coves</div></div>
              <div><div className="wv-stat-num">Nov–Apr</div><div className="wv-stat-label">Best season</div></div>
            </div>
            <Link to="/menu" className="wv-spotlight-btn" data-testid="spotlight-restaurant-link">Visit Benz Eatery — Best food on the island →</Link>
          </div>
        </div>
      </section>
      )}

      {/* Travel tips / Popular — admin-togglable */}
      {(sections.travel_tips || sections.popular) && (
      <section className="wv-two-col" id="tips" data-testid="tips-section">
        <div>
          <div className="wv-section-head" style={{ marginBottom: 20 }}>
            <h2 className="wv-section-title">Vietnam in a nutshell</h2>
          </div>
          <div className="wv-tips-card">
            <h3>Planning essentials</h3>
            {[
              { icon: 'visa', title: 'Visa', desc: 'E-visa available for 80+ countries. 30–90 days. Apply online, 3 working days.' },
              { icon: 'currency', title: 'Currency', desc: 'Vietnamese Dong (VND). ~25,000 VND = 1 USD. Carry cash outside big cities.' },
              { icon: 'weather', title: 'Best time to visit', desc: 'November to April across most of the country. Phu Quoc peaks Dec–Mar.' },
              { icon: 'transport', title: 'Getting around', desc: 'Trains north–south, domestic flights, Grab in cities. Motorbike only if experienced.' },
              { icon: 'language', title: 'Language', desc: 'Vietnamese. English common in tourism. "Xin chào" (hello) goes a long way.' },
            ].map(tip => (
              <div className="wv-tip" key={tip.title}>
                <div className="wv-tip-icon">
                  {tip.icon === 'visa' && <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M2 10h20"/><path d="M7 15h4"/></svg>}
                  {tip.icon === 'currency' && <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>}
                  {tip.icon === 'weather' && <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>}
                  {tip.icon === 'transport' && <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 17a2 2 0 1 1-4 0 2 2 0 0 1 4 0z"/><path d="M19 17a2 2 0 1 1-4 0 2 2 0 0 1 4 0z"/><path d="M13 6h5l3 5v6h-2"/><path d="M9 17H5V6a1 1 0 0 1 1-1h6v12"/></svg>}
                  {tip.icon === 'language' && <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>}
                </div>
                <div><strong>{tip.title}</strong><span>{tip.desc}</span></div>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="wv-section-head" style={{ marginBottom: 20 }}>
            <h2 className="wv-section-title">Popular now</h2>
          </div>
          <div className="wv-tips-card">
            {posts.slice(0, 4).map((post, i) => {
              const colors = [
                { bg: '#fde8e7', color: '#a31712' },
                { bg: '#fff4d4', color: '#7a5b00' },
                { bg: '#e4f2ea', color: '#1a6e45' },
                { bg: '#e3edf9', color: '#134c8c' },
              ];
              return (
                <Link to={`/blog/${post.id}`} className="wv-tip" key={post.id} data-testid={`popular-${post.id}`}>
                  <div className="wv-tip-icon" style={{ background: colors[i % 4].bg, color: colors[i % 4].color, fontWeight: 700, fontSize: 13 }}>
                    {String(i + 1).padStart(2, '0')}
                  </div>
                  <div><strong>{post.title}</strong><span>Published {fmtDate(post.created_at)}</span></div>
                </Link>
              );
            })}
            {posts.length === 0 && <p style={{ color: '#9a9590', fontSize: 14 }}>No stories yet.</p>}
          </div>
          {/* Ad: Sidebar in popular section */}
          <AdUnit variant="sidebar" />
        </div>
      </section>
      )}

      {/* Attractions section disabled (restaurant-only mode) */}

      {/* Newsletter — admin-togglable */}
      {sections.newsletter && (
      <section className="wv-newsletter" data-testid="newsletter-section">
        <div className="wv-newsletter-inner">
          <div>
            <h2>Get Vietnam in your inbox</h2>
            <p>One email every Sunday — new guides, seasonal tips, and hidden spots we don't share anywhere else.</p>
          </div>
          {nlStatus === 'success' ? (
            <div className="wv-nl-success" data-testid="newsletter-success">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><path d="M22 4L12 14.01l-3-3"/></svg>
              <span>You're in! Check your inbox for a welcome surprise.</span>
            </div>
          ) : (
            <form className="wv-newsletter-form" onSubmit={async (e) => {
              e.preventDefault();
              if (!nlEmail) return;
              setNlStatus('sending');
              try {
                const res = await fetch(`${API_URL}/api/newsletter/subscribe`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ email: nlEmail, source: 'landing_page' }),
                });
                const data = await res.json();
                setNlStatus(data.status === 'existing' ? 'existing' : 'success');
                if (data.status === 'existing') setTimeout(() => setNlStatus(''), 3000);
              } catch { setNlStatus('error'); setTimeout(() => setNlStatus(''), 3000); }
            }}>
              <input
                type="email"
                placeholder="your@email.com"
                value={nlEmail}
                onChange={(e) => setNlEmail(e.target.value)}
                required
                data-testid="newsletter-email"
              />
              <button type="submit" disabled={nlStatus === 'sending'} data-testid="newsletter-submit">
                {nlStatus === 'sending' ? '...' : nlStatus === 'existing' ? 'Already subscribed!' : nlStatus === 'error' ? 'Try again' : 'Subscribe'}
              </button>
            </form>
          )}
        </div>
      </section>
      )}

      <LocationPopup lang="en" />

      {/* Footer */}
      <footer className="wv-footer" data-testid="landing-footer">
        <div className="wv-footer-inner">
          <div className="wv-footer-brand">
            <div className="wv-logo"><div className="wv-logo-mark"><svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg></div>Wander Vietnam</div>
            <p>Your honest guide to Vietnam and Phu Quoc — written by locals and long-term travelers, updated every season.</p>
          </div>
          <div className="wv-footer-col">
            <h4>Destinations</h4>
            <a href="#destinations">Phu Quoc</a>
            <a href="#destinations">Ha Long Bay</a>
            <a href="#destinations">Hoi An</a>
            <a href="#destinations">Sapa</a>
            <a href="#destinations">Ho Chi Minh</a>
          </div>
          <div className="wv-footer-col">
            <h4>Guides</h4>
            <a href="#articles">Itineraries</a>
            <a href="#articles">Food & drink</a>
            <a href="#tips">Travel tips</a>
            <a href="#tips">Visa & entry</a>
          </div>
          <div className="wv-footer-col">
            <h4>More</h4>
            <Link to="/menu">Benz Eatery Menu</Link>
            <a href="#newsletter">Newsletter</a>
          </div>
        </div>
        <div className="wv-footer-bottom">
          <span>© {new Date().getFullYear()} Wander Vietnam. Made with <svg width="12" height="12" viewBox="0 0 24 24" fill="#da251d" style={{display:'inline',verticalAlign:'middle'}}><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg> in Hanoi & Phu Quoc.</span>
          <span>
            Dev by{' '}
            <a href="https://rigeneconsultancy.com" target="_blank" rel="noopener noreferrer" style={{ color: '#da251d', fontWeight: 500 }}>
              RCS
            </a>
          </span>
        </div>
      </footer>

      <style dangerouslySetInnerHTML={{ __html: STYLES }} />
    </div>
  );
};

const STYLES = `
  .wv-root {
    --bg: #f7f5f0;
    --surface: #ffffff;
    --text: #1a1815;
    --text-muted: #5c5650;
    --text-soft: #8a8580;
    --border: rgba(0,0,0,0.07);
    --accent: #c4291c;
    --accent-2: #e8a824;
    --tag-bg: #efebe2;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    background: var(--bg);
    color: var(--text);
    line-height: 1.6;
    -webkit-font-smoothing: antialiased;
  }
  .wv-root a { color: inherit; text-decoration: none; }
  .wv-root img { display: block; width: 100%; height: 100%; object-fit: cover; }

  .wv-announce {
    background: #1a1815;
    color: #d4d0c8;
    text-align: center;
    font-size: 12px;
    padding: 9px 16px;
    letter-spacing: 0.3px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .wv-announce svg { color: #d4d0c8; }
  .wv-announce-hl { color: var(--accent-2); font-weight: 600; }

  .wv-nav {
    position: sticky; top: 0; z-index: 50;
    background: rgba(247,245,240,0.92);
    backdrop-filter: blur(16px);
    border-bottom: 0.5px solid var(--border);
  }
  .wv-nav-inner {
    max-width: 1240px; margin: 0 auto;
    display: flex; align-items: center; justify-content: space-between;
    padding: 18px 32px;
  }
  .wv-logo {
    font-family: Georgia, serif;
    font-size: 22px; font-weight: 500;
    letter-spacing: -0.3px;
    display: flex; align-items: center; gap: 8px;
  }
  .wv-powered-by {
    display: block;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    font-size: 10px;
    font-weight: 400;
    letter-spacing: 0.3px;
    color: var(--text-soft);
    margin-top: -2px;
  }
  .wv-logo-mark {
    width: 30px; height: 30px;
    background: var(--accent);
    border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    color: var(--accent-2);
    font-size: 16px;
    flex-shrink: 0;
    overflow: hidden;
  }
  .wv-logo-img { width: 100%; height: 100%; object-fit: cover; }
  .wv-nav-links { display: flex; gap: 28px; font-size: 14px; }
  .wv-nav-links a { color: var(--text-muted); transition: color 0.15s; }
  .wv-nav-links a:hover { color: var(--text); }
  .wv-nav-links a.active { color: var(--text); font-weight: 500; }
  .wv-nav-actions { display: flex; gap: 12px; align-items: center; }
  .wv-search {
    display: flex; align-items: center; gap: 8px;
    background: var(--surface);
    border: 0.5px solid var(--border);
    border-radius: 20px;
    padding: 7px 14px;
    font-size: 13px; color: var(--text-muted);
    cursor: pointer;
  }
  .wv-plan-btn {
    background: var(--accent);
    color: white;
    border: none;
    padding: 8px 18px;
    border-radius: 20px;
    font-size: 13px; font-weight: 500;
    cursor: pointer;
    display: inline-block;
  }

  .wv-hero {
    max-width: 1240px; margin: 0 auto;
    padding: 24px 32px 0;
  }
  .wv-hero-slider {
    position: relative;
    height: 520px;
    border-radius: 20px;
    overflow: hidden;
  }
  .wv-hero-card {
    position: absolute; inset: 0;
    display: block;
    opacity: 0;
    transition: opacity 0.8s ease;
    pointer-events: none;
  }
  .wv-hero-card.active {
    opacity: 1;
    pointer-events: auto;
  }
  .wv-hero-card img { position: absolute; inset: 0; }
  .wv-hero-overlay {
    position: absolute; inset: 0;
    background: linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.12) 35%, rgba(26,24,21,0.82) 100%);
  }
  .wv-hero-content {
    position: absolute;
    bottom: 40px; left: 40px; right: 40px;
    color: white;
    max-width: 680px;
  }
  .wv-hero-kicker {
    display: inline-flex; align-items: center; gap: 8px;
    background: rgba(255,255,255,0.15);
    backdrop-filter: blur(8px);
    border: 0.5px solid rgba(255,255,255,0.3);
    padding: 6px 14px;
    border-radius: 20px;
    font-size: 12px;
    letter-spacing: 1px;
    text-transform: uppercase;
    margin-bottom: 18px;
  }
  .wv-hero-dot {
    width: 6px; height: 6px; border-radius: 50%;
    background: var(--accent-2);
    display: inline-block;
  }
  .wv-hero-content h1 {
    font-family: Georgia, serif;
    font-size: 48px;
    line-height: 1.1;
    font-weight: 500;
    margin-bottom: 16px;
    letter-spacing: -1px;
  }
  .wv-hero-content p {
    font-size: 17px;
    opacity: 0.9;
    margin-bottom: 24px;
    max-width: 560px;
  }
  .wv-hero-meta {
    display: flex; align-items: center; gap: 12px;
    font-size: 13px; opacity: 0.85;
  }
  .wv-avatar {
    width: 36px; height: 36px;
    border-radius: 50%;
    background: linear-gradient(135deg, #e8a824, #c4291c);
    display: flex; align-items: center; justify-content: center;
    color: white; font-size: 13px; font-weight: 500;
    border: 2px solid white;
    flex-shrink: 0;
  }
  .wv-avatar-sm { width: 28px; height: 28px; font-size: 11px; border: none; }

  /* Hero slider nav */
  .wv-hero-nav {
    position: absolute; bottom: 24px; right: 40px;
    display: flex; align-items: center; gap: 12px;
    z-index: 10;
  }
  .wv-hero-arrow {
    width: 36px; height: 36px; border-radius: 50%;
    background: rgba(255,255,255,0.15);
    backdrop-filter: blur(8px);
    border: 0.5px solid rgba(255,255,255,0.3);
    color: white; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    transition: background 0.2s;
  }
  .wv-hero-arrow:hover { background: rgba(255,255,255,0.3); }
  .wv-hero-dots { display: flex; gap: 6px; }
  .wv-hero-dot-btn {
    width: 8px; height: 8px; border-radius: 50%;
    border: none; cursor: pointer;
    background: rgba(255,255,255,0.35);
    transition: all 0.3s;
    padding: 0;
  }
  .wv-hero-dot-btn.active {
    background: white;
    width: 24px; border-radius: 4px;
  }

  .wv-explore {
    max-width: 1240px; margin: 0 auto;
    padding: 36px 32px 16px;
  }
  .wv-section-head {
    display: flex; align-items: baseline; justify-content: space-between;
    margin-bottom: 24px;
  }
  .wv-section-title {
    font-family: Georgia, serif;
    font-size: 28px;
    font-weight: 500;
    letter-spacing: -0.4px;
  }
  .wv-section-link {
    font-size: 14px; color: var(--text-muted);
    display: inline-flex; align-items: center; gap: 6px;
  }
  .wv-section-link:hover { color: var(--text); }
  .wv-destinations {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 14px;
  }
  .wv-dest {
    position: relative;
    height: 180px;
    border-radius: 14px;
    overflow: hidden;
    cursor: pointer;
    transition: transform 0.2s;
  }
  .wv-dest:hover { transform: translateY(-3px); }
  .wv-dest::after {
    content: ''; position: absolute; inset: 0;
    background: linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.7) 100%);
  }
  .wv-dest-label {
    position: absolute;
    bottom: 14px; left: 14px;
    color: white;
    z-index: 2;
  }
  .wv-dest-label strong {
    display: block;
    font-family: Georgia, serif;
    font-size: 17px;
    font-weight: 500;
  }
  .wv-dest-label span {
    font-size: 11px;
    opacity: 0.85;
    text-transform: uppercase;
    letter-spacing: 0.8px;
  }

  .wv-filter-section {
    max-width: 1240px; margin: 32px auto 0;
    padding: 0 32px 20px;
    border-bottom: 0.5px solid var(--border);
    display: flex; align-items: center; justify-content: space-between;
    flex-wrap: wrap; gap: 16px;
  }
  .wv-filters { display: flex; gap: 8px; flex-wrap: wrap; }
  .wv-filter-pill {
    padding: 7px 14px;
    border-radius: 16px;
    border: 0.5px solid var(--border);
    background: transparent;
    font-size: 13px;
    color: var(--text-muted);
    cursor: pointer;
    transition: all 0.15s;
    font-family: inherit;
  }
  .wv-filter-pill:hover { border-color: var(--text); color: var(--text); }
  .wv-filter-pill.active { background: var(--text); color: white; border-color: var(--text); }

  .wv-articles {
    max-width: 1240px; margin: 0 auto;
    padding: 32px 32px 40px;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 36px 28px;
  }
  .wv-article { cursor: pointer; display: block; }
  .wv-article-img {
    width: 100%;
    height: 220px;
    border-radius: 14px;
    overflow: hidden;
    margin-bottom: 18px;
    position: relative;
  }
  .wv-article-img img { transition: transform 0.4s; }
  .wv-article:hover .wv-article-img img { transform: scale(1.04); }
  .wv-tag {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 11px;
    margin-bottom: 10px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }
  .wv-article h2 {
    font-family: Georgia, serif;
    font-size: 21px;
    font-weight: 500;
    line-height: 1.3;
    margin: 0 0 8px;
    letter-spacing: -0.2px;
  }
  .wv-article p {
    color: var(--text-muted);
    font-size: 14px;
    margin-bottom: 14px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .wv-meta {
    display: flex; align-items: center; gap: 10px;
    font-size: 13px; color: var(--text-soft);
  }
  .wv-meta-author { color: var(--text); font-weight: 500; }
  .wv-meta-dot { color: var(--text-soft); }

  .wv-spotlight {
    max-width: 1240px; margin: 20px auto 40px;
    padding: 0 32px;
  }
  .wv-spotlight-inner {
    position: relative;
    border-radius: 20px;
    overflow: hidden;
    display: grid;
    grid-template-columns: 1.1fr 1fr;
    background: var(--text);
    min-height: 440px;
  }
  .wv-spotlight-img { position: relative; }
  .wv-spotlight-body {
    padding: 48px 44px;
    color: white;
    display: flex; flex-direction: column; justify-content: center;
  }
  .wv-spotlight-kicker {
    font-size: 12px;
    color: var(--accent-2);
    letter-spacing: 2px;
    text-transform: uppercase;
    margin-bottom: 14px;
    font-weight: 500;
  }
  .wv-spotlight-body h2 {
    font-family: Georgia, serif;
    font-size: 36px;
    font-weight: 500;
    line-height: 1.15;
    margin-bottom: 16px;
    letter-spacing: -0.5px;
  }
  .wv-spotlight-body p {
    color: rgba(255,255,255,0.75);
    font-size: 15px;
    margin-bottom: 28px;
  }
  .wv-spotlight-stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
    margin-bottom: 28px;
    padding-top: 24px;
    border-top: 0.5px solid rgba(255,255,255,0.15);
  }
  .wv-stat-num {
    font-family: Georgia, serif;
    font-size: 26px;
    font-weight: 500;
    color: var(--accent-2);
  }
  .wv-stat-label {
    font-size: 12px;
    color: rgba(255,255,255,0.6);
    margin-top: 2px;
  }
  .wv-spotlight-btn {
    align-self: flex-start;
    background: var(--accent-2);
    color: var(--text);
    border: none;
    padding: 12px 22px;
    border-radius: 22px;
    font-size: 14px; font-weight: 500;
    cursor: pointer;
    display: inline-block;
  }

  .wv-two-col {
    max-width: 1240px; margin: 0 auto;
    padding: 0 32px 40px;
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: 40px;
  }
  .wv-tips-card {
    background: var(--surface);
    border: 0.5px solid var(--border);
    border-radius: 16px;
    padding: 28px;
  }
  .wv-tips-card h3 {
    font-family: Georgia, serif;
    font-size: 20px;
    font-weight: 500;
    margin-bottom: 20px;
  }
  .wv-tip {
    display: flex;
    gap: 14px;
    padding: 14px 0;
    border-bottom: 0.5px solid var(--border);
    color: inherit;
  }
  .wv-tip:last-child { border-bottom: none; padding-bottom: 0; }
  .wv-tip:first-of-type { padding-top: 0; }
  .wv-tip-icon {
    width: 38px; height: 38px;
    border-radius: 10px;
    background: var(--tag-bg);
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
    font-size: 18px;
  }
  .wv-tip strong {
    display: block;
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 2px;
  }
  .wv-tip span {
    font-size: 13px;
    color: var(--text-muted);
  }

  .wv-newsletter {
    max-width: 1240px; margin: 0 auto 60px;
    padding: 0 32px;
  }
  .wv-newsletter-inner {
    background: linear-gradient(135deg, #c4291c 0%, #8c1b12 100%);
    color: white;
    border-radius: 20px;
    padding: 56px 48px;
    display: grid;
    grid-template-columns: 1.2fr 1fr;
    gap: 40px;
    align-items: center;
    position: relative;
    overflow: hidden;
  }
  .wv-newsletter-inner::before {
    content: '';
    position: absolute;
    right: -20px; top: -20px;
    width: 240px; height: 240px;
    background: var(--accent-2);
    opacity: 0.08;
    border-radius: 50%;
  }
  .wv-newsletter-inner::after {
    content: '';
    position: absolute;
    right: 60px; top: 40px;
    width: 120px; height: 120px;
    background: var(--accent-2);
    opacity: 0.06;
    border-radius: 50%;
  }
  .wv-newsletter-inner h2 {
    font-family: Georgia, serif;
    font-size: 34px;
    font-weight: 500;
    margin-bottom: 12px;
    letter-spacing: -0.5px;
  }
  .wv-newsletter-inner p {
    color: rgba(255,255,255,0.85);
    font-size: 15px;
  }
  .wv-newsletter-form {
    display: flex; gap: 8px;
    position: relative; z-index: 2;
  }
  .wv-newsletter-form input {
    flex: 1;
    padding: 14px 18px;
    border-radius: 24px;
    border: 1px solid rgba(255,255,255,0.25);
    background: rgba(255,255,255,0.1);
    color: white;
    font-size: 14px;
    outline: none;
    font-family: inherit;
  }
  .wv-newsletter-form input::placeholder { color: rgba(255,255,255,0.6); }
  .wv-newsletter-form button {
    padding: 14px 24px;
    border-radius: 24px;
    border: none;
    background: var(--accent-2);
    color: var(--text);
    font-size: 14px; font-weight: 500;
    cursor: pointer;
    font-family: inherit;
  }
  .wv-newsletter-form button:disabled { opacity: 0.6; cursor: not-allowed; }
  .wv-nl-success {
    display: flex; align-items: center; gap: 10px;
    background: rgba(74,222,128,0.1); border: 1px solid rgba(74,222,128,0.3);
    border-radius: 12px; padding: 14px 18px; color: #4ade80;
    font-size: 14px; font-weight: 600;
  }

  /* Attractions on landing */
  .wv-attractions { max-width:1240px; margin:0 auto; padding:48px 32px; }
  .wv-attractions-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:20px; }
  .wv-see-all { font-size:13px; color:#c4291c; font-weight:600; text-decoration:none; }
  .wv-see-all:hover { text-decoration:underline; }
  .wv-attractions-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:18px; }
  .wv-attraction-card { background:white; border-radius:14px; overflow:hidden; border:1px solid rgba(0,0,0,.06); text-decoration:none; color:inherit; transition:box-shadow .2s; }
  .wv-attraction-card:hover { box-shadow:0 6px 24px rgba(0,0,0,.08); }
  .wv-attraction-img { height:160px; overflow:hidden; background:#efebe2; position:relative; }
  .wv-attraction-img img { width:100%; height:100%; object-fit:cover; }
  .wv-attraction-placeholder { height:100%; background:#efebe2; }
  .wv-attraction-badge { position:absolute; top:10px; right:10px; background:rgba(0,0,0,.7); color:white; padding:3px 10px; border-radius:16px; font-size:10px; font-weight:600; }
  .wv-attraction-info { padding:14px 16px; }
  .wv-attraction-info h3 { font-size:15px; font-weight:700; margin:0 0 4px; font-family:Georgia,serif; }
  .wv-attraction-loc { font-size:11px; color:#8a8580; margin:0 0 6px; }
  .wv-attraction-price { font-size:14px; font-weight:800; color:#c4291c; }
  @media(max-width:768px) {
    .wv-attractions { padding:32px 16px; }
    .wv-attractions-grid { grid-template-columns:1fr; }
  }

  .wv-footer {
    border-top: 0.5px solid var(--border);
    background: var(--surface);
  }
  .wv-footer-inner {
    max-width: 1240px; margin: 0 auto;
    padding: 48px 32px 32px;
    display: grid;
    grid-template-columns: 2fr 1fr 1fr 1fr;
    gap: 40px;
  }
  .wv-footer-brand { max-width: 280px; }
  .wv-footer-brand p {
    font-size: 13px;
    color: var(--text-muted);
    margin-top: 12px;
    line-height: 1.6;
  }
  .wv-footer-col h4 {
    font-size: 13px;
    font-weight: 500;
    margin-bottom: 14px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: var(--text-muted);
  }
  .wv-footer-col a {
    display: block;
    font-size: 14px;
    color: var(--text);
    padding: 4px 0;
  }
  .wv-footer-bottom {
    max-width: 1240px; margin: 0 auto;
    padding: 20px 32px;
    border-top: 0.5px solid var(--border);
    display: flex; justify-content: space-between;
    font-size: 12px; color: var(--text-soft);
  }

  @media (max-width: 900px) {
    .wv-nav-inner { padding: 14px 16px; }
    .wv-logo { font-size: 18px; gap: 6px; }
    .wv-logo-mark { width: 26px; height: 26px; font-size: 13px; }
    .wv-powered-by { font-size: 9px; }
    .wv-nav-links { display: none; }
    .wv-search { display: none; }
    .wv-plan-btn { font-size: 12px; padding: 7px 14px; }

    .wv-hero { padding: 16px 16px 0; }
    .wv-hero-slider { height: 380px; border-radius: 14px; }
    .wv-hero-content { left: 20px; right: 20px; bottom: 24px; }
    .wv-hero-nav { bottom: 16px; right: 20px; }
    .wv-hero-content h1 { font-size: 28px; }
    .wv-hero-content h1 { font-size: 26px; letter-spacing: -0.5px; margin-bottom: 10px; }
    .wv-hero-content p { font-size: 14px; margin-bottom: 16px; }
    .wv-hero-kicker { font-size: 10px; padding: 5px 10px; margin-bottom: 12px; }
    .wv-hero-meta { font-size: 11px; gap: 8px; flex-wrap: wrap; }
    .wv-avatar { width: 28px; height: 28px; font-size: 10px; }

    .wv-explore { padding: 24px 16px 12px; }
    .wv-section-head { flex-direction: column; gap: 6px; margin-bottom: 16px; }
    .wv-section-title { font-size: 22px; }
    .wv-section-link { font-size: 13px; }
    .wv-destinations { grid-template-columns: repeat(2, 1fr); gap: 10px; }
    .wv-dest { height: 140px; border-radius: 10px; }
    .wv-dest:nth-child(5) { grid-column: 1 / -1; height: 160px; }
    .wv-dest-label { bottom: 10px; left: 10px; }
    .wv-dest-label strong { font-size: 15px; }
    .wv-dest-label span { font-size: 10px; }

    .wv-filter-section {
      margin: 32px auto 0;
      padding: 0 16px 16px;
      flex-direction: column;
      align-items: flex-start;
      gap: 12px;
    }
    .wv-filters { gap: 6px; }
    .wv-filter-pill { font-size: 12px; padding: 6px 12px; }

    .wv-articles {
      padding: 24px 16px 40px;
      grid-template-columns: 1fr;
      gap: 28px;
    }
    .wv-article-img { height: 200px; border-radius: 10px; }
    .wv-article h2 { font-size: 19px; }
    .wv-article p { font-size: 13px; }

    .wv-spotlight { padding: 0 16px; margin: 16px auto 32px; }
    .wv-spotlight-inner {
      grid-template-columns: 1fr;
      border-radius: 14px;
      min-height: auto;
    }
    .wv-spotlight-img { height: 220px; }
    .wv-spotlight-body { padding: 28px 24px; }
    .wv-spotlight-body h2 { font-size: 26px; }
    .wv-spotlight-body p { font-size: 14px; }
    .wv-spotlight-stats { gap: 12px; }
    .wv-stat-num { font-size: 20px; }
    .wv-stat-label { font-size: 11px; }
    .wv-spotlight-btn { font-size: 13px; padding: 10px 18px; }

    .wv-two-col {
      grid-template-columns: 1fr;
      padding: 0 16px 40px;
      gap: 24px;
    }
    .wv-tips-card { padding: 20px; border-radius: 12px; }
    .wv-tips-card h3 { font-size: 18px; margin-bottom: 14px; }
    .wv-tip-icon { width: 34px; height: 34px; font-size: 16px; }
    .wv-tip strong { font-size: 13px; }
    .wv-tip span { font-size: 12px; }

    .wv-newsletter { padding: 0 16px; margin: 0 auto 40px; }
    .wv-newsletter-inner {
      grid-template-columns: 1fr;
      padding: 32px 20px;
      border-radius: 14px;
      gap: 24px;
    }
    .wv-newsletter-inner h2 { font-size: 24px; }
    .wv-newsletter-inner p { font-size: 14px; }
    .wv-newsletter-form { flex-direction: column; }
    .wv-newsletter-form input { padding: 12px 16px; border-radius: 12px; }
    .wv-newsletter-form button { padding: 12px 20px; border-radius: 12px; }

    .wv-footer-inner {
      grid-template-columns: 1fr;
      padding: 32px 16px 24px;
      gap: 24px;
    }
    .wv-footer-brand { max-width: 100%; }
    .wv-footer-bottom {
      padding: 16px;
      flex-direction: column;
      gap: 8px;
      text-align: center;
    }

    .wv-ad-container { padding: 0 16px !important; }
  }

  @media (max-width: 480px) {
    .wv-hero-card { height: 340px; }
    .wv-hero-content h1 { font-size: 22px; }
    .wv-hero-content p { font-size: 13px; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
    .wv-destinations { grid-template-columns: repeat(2, 1fr); }
    .wv-dest { height: 120px; }
    .wv-dest:nth-child(5) { height: 130px; }
  }
`;

export default LandingPage;
